import React from 'react'
import { api } from 'components/api'
import { reviewRecordEmailPath, reviewAssociateUserPath } from '../routes/api'
import EnterEmail from './pages/EnterEmail'
import EnterPassword from './pages/EnterPassword'
import SetPassword from './pages/SetPassword'
import EnterCode from './pages/EnterCode'
import SignUpPage1 from './pages/SignUpPage1'
import SignUpPage2 from './pages/SignUpPage2'
import InvalidEmail from './pages/InvalidEmail'
import SsoSignIn from './pages/SsoSignIn'
import UserConfirmationModal from './UserConfirmationModal'
import PropTypes from 'prop-types'

class UnknownReviewerConfirmationModal extends React.Component {
  constructor (props) {
    super(props)

    this.state = this.initialState()
  }

  initialState = () => (
    {
      email: this.props.email,
      name: '',
      userStatus: null,
      userType: null,
      signInBlocked: false,
      hasPassword: false,
      alternateSignInUrl: null,
      confirmed: false
    }
  )

  // Do not give SsoSignIn an onSetPassword prop below. A password lets this person avoid
  // SSO.
  pageAfterEnterEmail = ({ userStatus, userType, signInBlocked, hasPassword, alternateSignInUrl }) => {
    let nextPage

    if (userType === 'supplier' || ['rejected', 'disabled'].includes(userStatus)) {
      nextPage = 'InvalidEmail'
    } else if (signInBlocked || (alternateSignInUrl && !hasPassword)) {
      nextPage = 'SsoSignIn'
    } else if (['verified', 'self_registered'].includes(userStatus)) {
      nextPage = 'EnterPassword'
    } else if (['approved', 'phonerep_added', 'preloaded'].includes(userStatus)) {
      nextPage = 'EnterCode'
    } else {
      nextPage = 'SignUpPage1'
    }

    return nextPage
  }

  handleModalClose = () => {
    if (!this.state.confirmed) {
      this.setState(this.initialState())
    }
  }

  submitReviewForm = () => {
    const form = document.getElementById('review-form')
    window.Rails.fire(form, 'submit')
  }

  signUpPageSubHeaderMessage = () => (
    <p className='message'>
      Please provide your information to continue submitting your review.
    </p>
  )

  captureReviewSource = ({ email, userId }) => {
    if (userId) {
      return api.patchWithCsrf({
        url: reviewAssociateUserPath(this.props.reviewId),
        data: {
          user_id: userId,
          user_identity_unverified: true
        }
      })
    } else {
      return api.patchWithCsrf({
        url: reviewRecordEmailPath(this.props.reviewId),
        data: { email }
      })
    }
  }

  markVerified = ({ userId }) => {
    return api.patchWithCsrf({
      url: reviewAssociateUserPath(this.props.reviewId),
      data: {
        user_id: userId,
        user_identity_unverified: false
      }
    })
  }

  renderPage = ({ currentPage, advanceToPage, goBackToPreviousPage, close }) => {
    const { reviewId } = this.props
    switch (currentPage) {
      case 'EnterEmail':
        return (
          <EnterEmail
            email={this.state.email}
            supportEmail={this.props.supportEmail}
            loginFormMessage={
              <p className='message'>One last step! You're moments away from submitting your review.</p>
          }
            createAccountMessage={
              <p className='message'>One last step! Create your account to submit your review.</p>
          }
            onSuccess={(data) => {
              this.setState(data, () => {
                const nextPage = this.pageAfterEnterEmail(this.state)

                // Route first. Then record. captureReviewSource attaches the email to the
                // review. This email goes to SSO, so do not attach it.
                if (nextPage === 'SsoSignIn') {
                  advanceToPage(nextPage)
                  return
                }

                this.captureReviewSource(this.state).then(() => {
                  advanceToPage(nextPage)
                })
              })
            }}
            onCreateAccount={() => {
              close()
              $('#sign-up-modal').foundation('open')
            }}
          />
        )
      case 'EnterPassword':
        return (
          <EnterPassword
            email={this.state.email}
            message={
              <>
                <p className='message'>One last step! You're moments away from submitting your review.</p>
                <p className='message'>Enter the password for your <b>{this.state.email}</b> account.</p>
              </>
          }
            onGoBack={goBackToPreviousPage}
            onSuccess={() => {
              this.markVerified(this.state).then(() => {
                close()
                this.submitReviewForm()
              })
            }}
            onResetPassword={() => advanceToPage('EnterCode')}
          />
        )
      case 'EnterCode':
        return (
          <EnterCode
            email={this.state.email}
            supportEmail={this.props.supportEmail}
            message={
              <p className='message'>
                We emailed a code to your email address <b>{this.state.email}</b>. Please enter the code to finish
                submitting
                your review.
              </p>
          }
            reviewId={reviewId}
            onSuccess={() => {
              this.setState({ confirmed: true }, () => {
                this.markVerified(this.state).then(() => {
                  advanceToPage('SetPassword')
                })
              })
            }}
          />
        )
      case 'SetPassword':
        return (
          <SetPassword
            email={this.state.email}
            supportEmail={this.props.supportEmail}
            onSuccess={() => {
              close()
              this.submitReviewForm()
            }}
          />
        )
      case 'SignUpPage1':
        return (
          <SignUpPage1
            email={this.state.email}
            emailDisabled
            name={this.state.name}
            subHeaderMessage={this.signUpPageSubHeaderMessage()}
            onGoBack={goBackToPreviousPage}
            onSuccess={(data) => {
              this.setState(data)
              advanceToPage('SignUpPage2')
            }}
          />
        )
      case 'SignUpPage2':
        return (
          <SignUpPage2
            email={this.state.email}
            name={this.state.name}
            subHeaderMessage={this.signUpPageSubHeaderMessage()}
            onGoBack={goBackToPreviousPage}
            onSuccess={(data) => {
              this.setState(data, () => {
                this.captureReviewSource(this.state).then(() => {
                  advanceToPage('EnterCode')
                })
              })
            }}
          />
        )
      case 'SsoSignIn':
        return (
          <SsoSignIn
            ssoSignInUrl={this.state.alternateSignInUrl}
            supportEmail={this.props.supportEmail}
          />
        )
      case 'InvalidEmail':
        return (
          <InvalidEmail
            headerMessage='Review cannot be submitted'
            subHeaderMessage={<>Your account does not have permission to post reviews on Procurated.</>}
            supportEmail={this.props.supportEmail}
          />
        )
    }
  }

  render () {
    return (
      <UserConfirmationModal
        render={this.renderPage}
        initialPage={this.props.page}
        modalId={this.props.modalId}
        onModalClose={this.handleModalClose}
      />
    )
  }
}

UnknownReviewerConfirmationModal.propTypes = {
  email: PropTypes.string.isRequired,
  reviewId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  page: PropTypes.string.isRequired,
  supportEmail: PropTypes.string.isRequired,
  modalId: PropTypes.string.isRequired
}

UnknownReviewerConfirmationModal.defaultProps = {
  email: '',
  page: 'EnterEmail'
}

export default UnknownReviewerConfirmationModal
